import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Clock, ShieldCheck, MapPin, Star, BadgeCheck, Wrench, ArrowUpRight, Check } from "lucide-react";
import { api, PHONE, PHONE_INTL, IMAGES } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Reveal, KineticLines, Stat } from "../components/Reveal";
import { CallButton, BrandMarquee } from "../components/Layout";
import { FaqAccordion, CtaBand, ICONS } from "../components/Shared";
import ContactForm from "../components/ContactForm";
import BrandsSection from "../components/BrandsSection";
import ArticlesTeaser from "../components/ArticlesTeaser";
import ReviewsSection from "../components/Reviews";

const CHAPTERS = [
  { n: "01", title: "Transparence", text: "Un devis gratuit et clair vous est communiqué avant chaque intervention. Aucun frais n'est engagé sans votre accord — jamais de mauvaise surprise sur la facture." },
  { n: "02", title: "Rapidité", text: "Nos équipes couvrent en permanence la Seine-et-Marne. Un serrurier se met en route dès votre appel et arrive en 20 à 30 minutes, de jour comme de nuit, 7j/7." },
  { n: "03", title: "Qualité", text: "Serrures certifiées A2P, techniciens expérimentés et pièces garanties. Nos interventions répondent aux exigences des compagnies d'assurance." },
];

const REVIEWS = [
  { name: "Camille D.", city: "Melun", text: "Porte claquée à 23h, le serrurier était là en 25 minutes et a ouvert sans rien casser. Impeccable et tarif annoncé à l'avance." },
  { name: "Karim B.", city: "Meaux", text: "Changement de serrure après un cambriolage. Rapides, pro et rassurants. Ils ont même fourni le devis pour l'assurance." },
  { name: "Sophie L.", city: "Chelles", text: "Blindage de porte parfait, travail très soigné. Je me sens beaucoup plus en sécurité. Je recommande vivement." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 hero-grid overflow-hidden" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-7">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" /> Dépannage d'urgence · 7j/7 · Seine-et-Marne 77
          </motion.div>
          <KineticLines
            lines={["Serrurier d'urgence", "en Seine-et-Marne", <span key="c" className="text-primary">en 30 minutes.</span>]}
            className="font-display font-semibold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-zinc-900"
          />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-7 max-w-xl text-lg text-zinc-600 leading-relaxed">
            Ouverture de porte, changement de serrure, blindage et mise en sécurité. Un serrurier professionnel intervient rapidement partout dans le 77, jour et nuit.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <a href={`tel:${PHONE_INTL}`} data-testid="hero-call" className="group flex items-center gap-3 rounded-full bg-primary pl-6 pr-3 py-3 text-white shadow-lg shadow-primary/30 hover:scale-[1.03] transition-transform duration-200">
              <span className="text-left">
                <span className="block text-[10px] font-mono uppercase tracking-widest text-white/70">Appeler maintenant</span>
                <span className="block font-display font-semibold text-xl tracking-tight">{PHONE}</span>
              </span>
              <span className="grid place-items-center h-11 w-11 rounded-full bg-white"><Phone className="h-5 w-5 text-primary" /></span>
            </a>
            <Link to="/contact" data-testid="hero-quote" className="rounded-full border border-zinc-300 bg-white px-6 py-4 text-sm font-medium text-zinc-800 hover:border-primary hover:text-primary transition-colors">Devis gratuit en ligne</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-600">
            {["Intervention rapide", "Devis gratuit", "Intervention 7j/7"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {t}</span>
            ))}
          </motion.div>
        </div>

        <Reveal delay={0.15} className="relative">
          <motion.div style={{ y }} className="relative rounded-3xl overflow-hidden shadow-2xl shadow-zinc-300/60 border border-zinc-200">
            <img src={IMAGES.hero} alt="Serrurier SERRURERIE 77 en intervention sur une serrure en Seine-et-Marne" className="w-full h-[420px] sm:h-[520px] object-cover" />
          </motion.div>
          <div className="absolute -bottom-5 -left-2 sm:-left-5 rounded-2xl bg-white shadow-xl border border-zinc-100 px-5 sm:px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-11 w-11 rounded-full bg-primary/10"><Clock className="h-5 w-5 text-primary" /></div>
              <div>
                <div className="font-display font-semibold text-xl text-zinc-900 leading-none">~30 min</div>
                <div className="text-xs text-zinc-500 mt-1">Délai moyen d'intervention</div>
              </div>
            </div>
          </div>
          <div className="hidden sm:block absolute -top-5 -right-3 rounded-2xl bg-white shadow-xl border border-zinc-100 px-5 py-3">
            <div className="flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
            <div className="text-xs text-zinc-500 mt-1">4,9/5 · 1 277 avis</div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const BENEFITS = [
  { icon: Clock, title: "Intervention 30 min", text: "Délai moyen dans tout le 77" },
  { icon: ShieldCheck, title: "Devis gratuit", text: "Tarif annoncé avant intervention" },
  { icon: MapPin, title: "7j/7 · 24h/24", text: "Y compris nuits et jours fériés" },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [faq, setFaq] = useState([]);
  const [cities, setCities] = useState([]);
  useSEO({ title: "Serrurier Seine-et-Marne (77) — Dépannage Urgent 7j/7 | Devis Gratuit", description: `Serrurier d'urgence en Seine-et-Marne (77). Ouverture de porte, changement de serrure, blindage. Intervention en 30 min, 7j/7. Devis gratuit ☎ ${PHONE}.`, path: "/", faq, keywords: ["serrurier Seine-et-Marne", "serrurier 77", "dépannage serrurier 77", "urgence serrurier Seine-et-Marne", "ouverture de porte 77", "changement de serrure Seine-et-Marne", "blindage de porte 77", "serrurier 7j/7 24h/24", `serrurier ${PHONE}`] });

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/faq").then(r => setFaq(r.data)).catch(() => {});
    api.get("/cities").then(r => setCities(r.data)).catch(() => {});
  }, []);

  const bento = services.filter(s => ["ouverture-de-porte", "blindage-de-porte", "changement-de-serrure", "reparation-apres-effraction", "depannage-serrurerie"].includes(s.slug));

  return (
    <div className="bg-white">
      <Hero />

      <section className="relative z-10 border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid sm:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <Reveal key={i} delay={i * 0.08} className={`flex items-center gap-4 py-8 ${i > 0 ? "sm:border-l border-zinc-200 sm:pl-8" : ""}`}>
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-primary/10 shrink-0"><b.icon className="h-6 w-6 text-primary" /></div>
              <div><div className="font-semibold text-zinc-900">{b.title}</div><div className="text-sm text-zinc-500">{b.text}</div></div>
            </Reveal>
          ))}
        </div>
      </section>

      <BrandMarquee />

      <section className="relative z-10 py-24 sm:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="overline mb-4">Nos prestations</p>
              <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter max-w-2xl leading-[1.02] text-zinc-900">Un service pour chaque situation d'urgence</h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/services" className="text-sm text-zinc-600 hover:text-primary flex items-center gap-1.5 link-underline">Tous les services <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            <BentoCard service={bento.find(s=>s.slug==="depannage-serrurerie")} img={IMAGES.van} className="md:col-span-8 md:row-span-2 min-h-[340px]" big />
            <BentoCard service={bento.find(s=>s.slug==="ouverture-de-porte")} img={IMAGES.techHandle} className="md:col-span-4 min-h-[260px]" />
            <BentoCard service={bento.find(s=>s.slug==="blindage-de-porte")} img={IMAGES.handleDark} className="md:col-span-4 min-h-[260px]" />
            <BentoCard service={bento.find(s=>s.slug==="changement-de-serrure")} img={IMAGES.cylinder} className="md:col-span-6 min-h-[260px]" />
            <BentoCard service={bento.find(s=>s.slug==="reparation-apres-effraction")} img={IMAGES.tech} className="md:col-span-6 min-h-[260px]" />
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24 sm:py-32 border-t border-zinc-200 bg-zinc-50" data-testid="manifesto">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[0.9fr_1.5fr] gap-16">
          <div className="lg:sticky lg:top-28 h-fit">
            <p className="overline mb-4">Notre engagement</p>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter leading-none text-zinc-900">Pourquoi<br/>nous faire<br/>confiance</h2>
            <div className="mt-10 grid grid-cols-3 gap-4">
              <Stat value="30min" label="Délai moyen" />
              <Stat value="12k+" label="Interventions" />
              <Stat value="4.9/5" label="Avis clients" />
            </div>
          </div>
          <div className="space-y-4">
            {CHAPTERS.map((c, i) => (
              <Reveal key={i} delay={i * 0.05} className="card-soft rounded-2xl border border-zinc-200 bg-white p-8 sm:p-10 shadow-sm">
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-primary text-sm">{c.n}</span>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-display font-medium mb-3 text-zinc-900">{c.title}</h3>
                    <p className="text-zinc-600 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />

      <section className="relative z-10 py-24 sm:py-32 border-t border-zinc-200 bg-zinc-50" data-testid="cities-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline mb-4">Zones d'intervention</p>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter max-w-2xl leading-[1.02] text-zinc-900">Un serrurier près de chez vous en Seine-et-Marne</h2>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-3">
            {cities.slice(0, 24).map((c) => (
              <Link key={c.slug} to={`/serrurier/${c.slug}`} data-testid={`city-chip-${c.slug}`}
                className="card-soft rounded-full border border-zinc-200 bg-white px-5 py-2.5 text-sm text-zinc-700 hover:text-primary flex items-center gap-2 shadow-sm">
                <MapPin className="h-3.5 w-3.5 text-primary" /> {c.name}
              </Link>
            ))}
            <Link to="/villes" className="rounded-full bg-primary px-5 py-2.5 text-sm text-white shadow-sm hover:scale-105 transition-transform">+ Toutes les villes →</Link>
          </div>
        </div>
      </section>

      <CtaBand />

      <BrandsSection />

      <section className="relative z-10 py-24 sm:py-32 border-t border-zinc-200 bg-white" data-testid="home-contact">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <p className="overline mb-4">Devis gratuit</p>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter leading-none text-zinc-900">Demandez votre devis en 2 minutes</h2>
            <p className="text-zinc-600 mt-6 max-w-md leading-relaxed">Décrivez votre besoin, nous vous rappelons rapidement avec un tarif clair. Pour une urgence, appelez directement.</p>
            <div className="mt-8"><CallButton className="px-7 py-4 text-base" testid="contact-section-call" /></div>
          </Reveal>
          <Reveal delay={0.1}><ContactForm services={services} cities={cities} /></Reveal>
        </div>
      </section>

      <FaqAccordion items={faq} subtitle="Tout ce que vous devez savoir avant de faire appel à un serrurier." />

      <ArticlesTeaser subtitle="Nos guides pour sécuriser votre logement et prévenir les cambriolages." className="bg-zinc-50" />
    </div>
  );
}

const BentoCard = ({ service, img, className = "", big }) => {
  if (!service) return null;
  const Icon = ICONS[service.icon] || Wrench;
  return (
    <Link to={`/services/${service.slug}`} data-testid={`bento-${service.slug}`}
      className={`group relative overflow-hidden rounded-2xl border border-zinc-200 shadow-sm ${className}`}>
      <img src={img} alt={`${service.name} par SERRURERIE 77 en Seine-et-Marne`} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-7">
        {service.priority && <span className="absolute top-6 left-6 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">Urgence n°1</span>}
        <Icon className="h-8 w-8 text-white mb-4" />
        <h3 className={`font-display font-medium tracking-tight text-white ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{service.name}</h3>
        <p className="text-zinc-200 text-sm mt-2 max-w-md">{service.tagline}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/0 group-hover:text-white transition-colors">Voir le service <ArrowUpRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );
};
